import React, { useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Box, Stack, Typography, Paper, Divider, Chip } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import ButtonLoading from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useListAllSystemStatusDropdown,
  useSystemDetails,
  useSystemMemberListDropdown,
  useUpdateSystem,
  useUsersForDeploySystem,
} from 'services/v1/TenantMaster/MasterTable/SystemTableDataService';
import { MEMBER_LIST_STATIC_RECORD } from 'data/MemberListRecord';
import { useServerListDropdown } from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { DropdownCreatableMemberList } from 'pages/Tenant/AROKMS/DataInput/components/DropdownCreatableMemberList';
import { Field, Option } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { UpdateSystemValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/SystemTableValidationScheman';
import { useParams, useSearchParams } from 'react-router-dom';
import { SystemDetails } from '../../../../types/api/TenantMaster/MasterTable/SystemTableDataTypes';
import ActivityIndicator from '../../../../components/ActivityIndicatorComponent';

const dataGridStyle = {
  '& .MuiCheckbox-root svg': {
    backgroundColor: 'transparent',
    color: '#98A2AE',
    borderRadius: 2,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#EDF2FF',
    borderTop: '1px solid #E3EBF6',
    borderBottom: '1px solid #E3EBF6',
    textAlign: 'center',
    display: 'flex',
  },
};

const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

const textInputAutoCompleteStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const SYSTEM_DATA_SOURCE = {
  SCRATCH: 'SCRATCH',
  COPY_FROM_EXISTING_SYSTEM: 'COPY_FROM_EXISTING_SYSTEM',
};

const optionStyles = { '& > span': { fontSize: '14px', mr: 2, flexShrink: 0 } };

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

const disabledInputStyles = {
  '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: '#F9FAFB' },
};

const formInitialValues: SystemDetails = {
  serverId: { value: '', label: '' },
  assignUsers: [],
  system: { value: '', label: '' },
  codeStatus: { value: '', label: '' },
  demoStatus: { value: '', label: '' },
  fieldStatus: { value: '', label: '' },
  dataSource: SYSTEM_DATA_SOURCE.SCRATCH,
  allowChangeFieldStatus: true,
  sourceSystemId: null,
  description: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

function optionRenderInput(props: React.HTMLAttributes<HTMLLIElement>, option: DropdownItem) {
  return (
    <Box component='li' sx={optionStyles} {...props}>
      <span>{option.label}</span>
    </Box>
  );
}

export default function EditSystemPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  const { mutate: updateSystem, isLoading: isDeploying, error, isError } = useUpdateSystem();

  const handleOnCancelInput = () => {
    const url =
      searchParams.get('refBackParam') != null
        ? searchParams.get('refBackParam')
        : PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.SYSTEM;
    const redirectPath = decodeURIComponent(url as string);
    navigate(redirectPath);
  };

  const handleOnSave = (data: SystemDetails) => {
    const payloadData = {
      codeStatus: data.codeStatus?.value || '',
      fieldStatus: data.fieldStatus?.value || '',
      demoStatus: data.demoStatus?.value || '',
      serverId: data.serverId?.value || '',
      system: data.system?.value || '',
      assignUsers: data.assignUsers,
      description: data.description,
      id,
    };
    updateSystem(payloadData, {
      onSuccess: () => {
        handleOnCancelInput();
        toast.success('System has been updated successfully!');
      },
    });
  };

  const formik = useFormik<SystemDetails>({
    initialValues: formInitialValues,
    validationSchema: UpdateSystemValidationSchema,
    onSubmit: handleOnSave,
  });

  const { data: tenantDetails, isLoading } = useSystemDetails(id);
  const { data: usersData, isLoading: isLoadingUsers } = useUsersForDeploySystem(formik.values.system?.value);
  const { data: serverOptions } = useServerListDropdown();
  const { data: systemMemberList, isLoading: isFetchingSystem } = useSystemMemberListDropdown();
  const { data: optionsStatus, isLoading: isFetchingOptionStatus } = useListAllSystemStatusDropdown();

  const disableDropdownStatus = useMemo(() => {
    return {
      codeStatus: formik.values.fieldStatus?.value !== MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED,
      demoStatus:
        formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED &&
        formik.values.fieldStatus?.value !== MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED,
      fieldStatus:
        formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED &&
        formik.values.demoStatus?.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED,
    };
  }, [formik.values]);

  const dropdownCreateableMemberListField = {
    name: 'system',
    options: (systemMemberList?.data as Option[]) || [],
    label: 'System',
    placeholder: 'Please Select',
    colSubjectId: '4028819c84e5373a0184e5373a3d0000',
    disabled: true,
  } as Field;

  const isAnyLoading = isFetchingSystem || isFetchingOptionStatus;

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'Username', width: 180 },
    { field: 'roleName', headerName: 'Role Name', width: 180 },
    { field: 'userName', headerName: 'Email', width: 240 },
  ];

  const selectedIds = useMemo(() => {
    if (formik.values.assignUsers) {
      return formik.values.assignUsers.map((user) => user.userId);
    }
    return [];
  }, [formik.values.assignUsers]);

  useEffect(() => {
    if (tenantDetails) {
      const data: any = { ...tenantDetails.data };
      if ((!data.assignUsers || data.assignUsers.length === 0) && data.userId && Array.isArray(data.userId)) {
        data.assignUsers = data.userId.map((uid: string) => ({ userId: uid, roleId: '' }));
      }
      formik.setValues(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantDetails]);

  useEffect(() => {
    if (usersData?.data && formik.values.assignUsers && formik.values.assignUsers.length > 0) {
      const hasMissingRoleId = formik.values.assignUsers.some((u) => !u.roleId);
      if (hasMissingRoleId) {
        let changed = false;
        const updatedAssignUsers = formik.values.assignUsers.map((u) => {
          if (!u.roleId) {
            const userInGrid = usersData.data.find((row) => row.id === u.userId);
            if (userInGrid?.roleId) {
              changed = true;
              return { ...u, roleId: userInGrid.roleId };
            }
          }
          return u;
        });

        if (changed) {
          formik.setFieldValue('assignUsers', updatedAssignUsers);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersData?.data, formik.values.assignUsers]);

  useEffect(() => {
    if (
      formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.CHANGING &&
      formik.values.demoStatus?.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED
    ) {
      formik.setFieldValue(
        'demoStatus',
        optionsStatus?.data.demoStatus.find((item) => item.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.CHANGING),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.codeStatus, formik.values.demoStatus, optionsStatus?.data.demoStatus]);

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        {isLoading ? (
          <Stack direction='column' sx={{ height: 500 }} justifyContent='center'>
            <ActivityIndicator />
          </Stack>
        ) : (
          <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
            {/* Page Header */}
            <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
              <Box>
                <Stack direction='row' alignItems='center' spacing={1.5}>
                  <Typography
                    variant='body1'
                    component='h2'
                    fontWeight='bold'
                    sx={{ color: '#3B4797', fontSize: 18 }}
                  >
                    Edit System
                  </Typography>
                  {formik.values.system?.label && (
                    <Chip
                      label={formik.values.system.label}
                      size='small'
                      sx={{ backgroundColor: '#EDF2FF', color: '#3B4797', fontWeight: 500, fontSize: 12 }}
                    />
                  )}
                </Stack>
                <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                  Update system settings, status configuration, and builder assignments. The system name and server
                  cannot be changed after creation.
                </Typography>
              </Box>
              <Button
                size='small'
                onClick={handleOnCancelInput}
                variant='main-table-panel-border'
                disabled={isDeploying}
              >
                Cancel
              </Button>
            </Stack>

            {/* Section 1: System Details */}
            <Paper sx={sectionPaperStyles}>
              <SectionHeader
                title='System Details'
                description='The system identity and deployment server were set during creation. Only the description can be modified here.'
              />
              <Divider sx={{ mb: 1 }} />

              <DropdownCreatableMemberList<SystemDetails>
                required
                formik={formik}
                field={dropdownCreateableMemberListField}
                sx={{ width: '100%' }}
              />

              <FormField label='Description'>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Enter a brief description of this system'
                  name='description'
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </FormField>

              <FormField label='Server' required>
                <Autocomplete
                  options={serverOptions || []}
                  // @ts-ignore
                  value={formik.values.serverId}
                  disabled
                  getOptionLabel={(option) => option.label}
                  clearIcon={null}
                  contentEditable={false}
                  onChange={(event, value) => {
                    formik.setFieldValue('serverId', value);
                  }}
                  renderOption={optionRenderInput}
                  sx={autocompleteStyles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ ...textInputAutoCompleteStyles, ...disabledInputStyles }}
                      placeholder='Select Server'
                      size='small'
                    />
                  )}
                />
              </FormField>
            </Paper>

            {/* Section 2: Status Configuration */}
            <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
              <SectionHeader
                title='Status Configuration'
                description='Manage the lifecycle status for code, demo, and field stages. Status progression follows a sequential lock flow — code must be locked before demo, and both must be locked before field can be released.'
              />
              <Divider sx={{ mb: 1 }} />

              <FormField label='Code Status' required>
                <Autocomplete
                  disabled={!disableDropdownStatus.codeStatus}
                  options={optionsStatus?.data.codeStatus || []}
                  value={formik.values.codeStatus}
                  getOptionLabel={(option: DropdownItem) => option.label}
                  clearIcon={null}
                  contentEditable={false}
                  onChange={(event, value) => {
                    formik.setFieldValue('codeStatus', value);
                  }}
                  renderOption={optionRenderInput}
                  sx={autocompleteStyles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={textInputAutoCompleteStyles}
                      placeholder='Select code status'
                      size='small'
                    />
                  )}
                />
              </FormField>

              <FormField label='Demo Status' required>
                <Autocomplete
                  options={optionsStatus?.data.demoStatus || []}
                  // @ts-ignore
                  value={formik.values.demoStatus}
                  disabled={!disableDropdownStatus.demoStatus}
                  getOptionLabel={(option) => option.label}
                  clearIcon={null}
                  contentEditable={false}
                  onChange={(event, value) => {
                    formik.setFieldValue('demoStatus', value);
                  }}
                  renderOption={optionRenderInput}
                  sx={autocompleteStyles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={textInputAutoCompleteStyles}
                      placeholder='Select demo status'
                      size='small'
                    />
                  )}
                />
              </FormField>

              <FormField label='Field Status' required>
                <Autocomplete
                  options={optionsStatus?.data.fieldStatus || []}
                  // @ts-ignore
                  value={formik.values.fieldStatus}
                  disabled={!disableDropdownStatus.fieldStatus}
                  getOptionLabel={(option) => option.label}
                  clearIcon={null}
                  contentEditable={false}
                  onChange={(event, value) => {
                    formik.setFieldValue('fieldStatus', value);
                  }}
                  renderOption={optionRenderInput}
                  sx={autocompleteStyles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={textInputAutoCompleteStyles}
                      placeholder='Select field status'
                      size='small'
                    />
                  )}
                />
              </FormField>
            </Paper>

            {/* Section 3: Builder Assignment */}
            <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
              <SectionHeader
                title='Builder Assignment'
                description='Manage which builders have access to configure and build within this system.'
              />
              <Divider sx={{ mb: 2 }} />

              <div style={{ height: 280, width: '100%' }}>
                {!isLoadingUsers && (
                  <DataGridPro
                    loading={isLoadingUsers}
                    selectionModel={selectedIds}
                    sx={dataGridStyle}
                    density='compact'
                    rows={usersData?.data || []}
                    columns={columns}
                    onSelectionModelChange={(newSelection) => {
                      if (usersData?.data) {
                        const newAssignUsers = newSelection.map((id) => {
                          const user = usersData.data.find((u) => u.id === id);
                          return { userId: id as string, roleId: user?.roleId || '' };
                        });
                        formik.setFieldValue('assignUsers', newAssignUsers);
                      }
                    }}
                    pagination={false}
                    hideFooter
                    rowsPerPageOptions={[5]}
                    checkboxSelection={!isAnyLoading || (!!formik.values.system && !!formik.values.serverId)}
                  />
                )}
              </div>
              {selectedIds.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#3B4797', mt: 1, fontWeight: 500 }}>
                  {selectedIds.length} builder{selectedIds.length > 1 ? 's' : ''} selected
                </Typography>
              )}
            </Paper>

            {/* Error Alert */}
            {isError && (
              <Alert severity='error' sx={{ mt: 2.5, borderRadius: 2 }}>
                {error.response?.data.message}
              </Alert>
            )}

            {/* Action Buttons */}
            <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
              <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
                Cancel
              </Button>
              <ButtonLoading
                loading={isDeploying}
                variant='main-table-panel'
                onClick={() => formik.handleSubmit()}
                disabled={!UpdateSystemValidationSchema?.isValidSync(formik.values) || isDeploying}
              >
                Save Changes
              </ButtonLoading>
            </Stack>
          </Box>
        )}
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
